import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "i18n";
import { RecurlyError } from "@recurly/recurly-js/lib/error";
import { Spinner } from "../../../../../../../Atoms/Spinner/Spinner";
import { CheckoutFlow, currencyMap, IAddOns } from "../../../../../types";
import RecurlyWrapper from "../../../../RecurlyWrapper";
import { useDynamicWindowWidthFor } from "shared-hooks";
import { addOnNamesMap } from "../../../../../constants";
import PriceInfoBlock from "../../../PriceInfoBlock";
import SummaryBlock from "../../../../SummaryBlock";
import { useGetExistingPaymentMethod } from "../../../../../hooks/useGetExistingPaymentMethod";
import UseExistingPaymentMethodBlock from "../../../../UseExistingPaymentMethodBlock/UseExistingPaymentMethodBlock";
import ExistingPaymentMethodBlock from "../../../../PaymentTypeMethodBlock/ExistingPaymentMethodBlock/ExistingPaymentMethodBlock";
import PaymentMethodButton from "../../../../PaymentTypeMethodBlock/PaymentMethodButton";
import { PaymentErrorModal } from "../../../../PaymentErrorModal";
import { usePreviewPurchase } from "../../hooks/usePreviewPurchase";
import { useSubmitPurchase } from "../../hooks/useSubmitPurchase";
import { useCpaOptIn } from "../../../../../hooks/useCpaOptIn";
import { AddOnPurchaseFormRef } from "../../types";
import { SCREEN_WIDTH_ENUM } from "../../../../../../../../generics/dom-extensions";
import { getPurchaseType } from "../../../../../utils/getPurchaseType";
import { useExistingMethodCountryCode } from "../../../../../hooks/useExistingMethodCountryCode";

interface Props {
  addOn: IAddOns;
  purchaseFormRef: AddOnPurchaseFormRef;
  onSuccessCallback: (trackingData?: any) => Promise<void>;
}

const AddOnPurchase: FC<React.PropsWithChildren<Props>> = ({
  addOn,
  purchaseFormRef,
  onSuccessCallback,
}) => {
  const isMobileView = useDynamicWindowWidthFor(SCREEN_WIDTH_ENUM.SM);
  const { t } = useTranslation();
  const [recurlyErrorMsg, setRecurlyErrorMsg] = useState("");
  const { mutate: optInCpa } = useCpaOptIn();
  const {
    data: existingPaymentMethodData,
    isLoading: existingPaymentMethodIsLoading,
  } = useGetExistingPaymentMethod(true);
  const existingPayMethodCountryCode = useExistingMethodCountryCode();

  const addOns = [addOn.code];
  const { data: previewPurchase, isLoading: previewPurchaseLoading } =
    usePreviewPurchase(addOns);
  const submitPurchase = useSubmitPurchase(addOns);

  const submitPayment = async () => {
    try {
      await submitPurchase();
      await onSuccessCallback({
        purchaseType: getPurchaseType(CheckoutFlow.SubscriptionsModals),
        paymentValue: previewPurchase?.amount,
        addOns,
      });
      optInCpa(true);
    } catch (error: unknown) {
      const recurlyError = error as RecurlyError;

      setRecurlyErrorMsg(
        recurlyError?.message || t("common:billing.genericErrorMessage")
      );
    }
  };

  useEffect(() => {
    if (purchaseFormRef) {
      purchaseFormRef.current = { submitPayment };
    }
  }, [submitPayment]);

  if (previewPurchaseLoading || existingPaymentMethodIsLoading)
    return <Spinner />;

  return (
    <div className="flex flex-col">
      {!!recurlyErrorMsg && (
        <PaymentErrorModal
          handleCloseModal={() => setRecurlyErrorMsg("")}
          title={t("common:billing.errorPopup.title")}
          description={recurlyErrorMsg}
        />
      )}
      {!isMobileView && (
        <PriceInfoBlock
          currencySymbol={currencyMap[addOn.currency]}
          value={addOn.amountValue}
          priceSuffix={t("common:billing.label.plusVat")}
          title={t(addOnNamesMap[addOn.code]).toUpperCase()}
          variant="secondary"
          key={addOn.code}
          pricePrefix={t("common:billing.ecp.pricePrefix")}
        />
      )}

      <div className="flex flex-col-reverse sm:flex-row gap-[30px]">
        <div className="flex-1 mt-[2rem]">
          <h2 className="mb-4 font-semibold text-xxl">
            {t("common:billing.ecp.paymentMethodTitle")}
          </h2>
          <PaymentMethodButton
            isSelected
            onSelect={() => {}}
            variant="secondary"
          >
            <ExistingPaymentMethodBlock data={existingPaymentMethodData} />
          </PaymentMethodButton>

          <h2 className="text-xxl font-semibold mb-4 mt-[30px]">
            {t("common:billing.ecp.paymentMethodDetailTitle")}
          </h2>
          <UseExistingPaymentMethodBlock
            data={existingPaymentMethodData}
            isLoading={existingPaymentMethodIsLoading}
          />
        </div>
        <div className="flex-1">
          <SummaryBlock
            isMobileView={isMobileView}
            planName={t(addOnNamesMap[addOn.code])}
            price={{
              total: previewPurchase?.total || "",
              amount: previewPurchase?.amount || "",
              discount: "",
              tax: previewPurchase?.tax || "",
              taxPercentage: previewPurchase?.taxPercentage || 0,
              subtotal: previewPurchase?.amount || "",
            }}
            couponBag={{
              hasAccountDiscount: false,
              userCouponCodesObject: [],
              setCoupon: async () => {},
              removeCoupon: async () => {},
              couponErrors: [],
              isCheckoutPreviewRequestLoading: previewPurchaseLoading,
            }}
            planNameTooltip={{
              header: "common:billing.ecp.planName.tooltipHeader",
              body: "common:billing.ecp.planName.tooltipBody",
            }}
            disclaimerMessage="common:billing.checkout.orderSummary.addOnContinuousAuthorityAgreement"
            countryCode={existingPayMethodCountryCode}
          />
        </div>
      </div>
    </div>
  );
};

export default ({ addOn, purchaseFormRef, onSuccessCallback }: Props) => {
  return (
    <RecurlyWrapper>
      <AddOnPurchase
        addOn={addOn}
        purchaseFormRef={purchaseFormRef}
        onSuccessCallback={onSuccessCallback}
      />
    </RecurlyWrapper>
  );
};
